import React from 'react';
import { Box, Grid, Typography, IconButton, Menu, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MoreVert, Close } from '@mui/icons-material';
import ModalComponent from 'ui-components/ModalComponent';
import { FetchReportDetails } from 'api/report';
import { downloadFile, getFileCategory } from 'utils';
import { DownloadDocumentIcon } from 'ui-components/icons/DocumentActionIcons';

/*
 * Report detail popup (Deal Flow → startup → Reports → "Open Report").
 * Built to the Figma. VIEWER-ONLY: no Edit/Send/Delete — only Close + Download.
 *
 * 🔴 Prepared-for-API, no fake data in the component itself. Every section reads
 * from the report object passed in (falling back to FetchReportDetails for real
 * reports). Financials live only in the caller's data:
 *   report.overview               -> Body (full HTML)
 *   report.documents              -> Attachments
 *   report.performance_snapshot   -> Performance Snapshot (hidden if absent)
 *
 * report.performance_snapshot shape (supplied by the backend later):
 *   { revenue|net_burn|runway: { value, delta, trend:'up'|'down', positive:bool, series:number[] } }
 * Presentation only (label/chart/colour) is fixed here per metric key.
 *
 * NOTE: hex/px below are measured from the Figma screenshot — flagged for exact values.
 */

// muted #667085, heading #101828, body #475467, green #12B76A, red #F04438 — from Figma
const MUTED = '#667085';
const HEADING = '#101828';
const BODY = '#475467';
const POS = '#12B76A';
const NEG = '#F04438';

// Presentation for each known snapshot metric (order + label + chart type + colour).
const SNAPSHOT_META = {
    revenue: { label: 'Revenue', chart: 'bar', color: '#2E6BE6' },
    net_burn: { label: 'Net Burn', chart: 'bar', color: '#7C3AED' },
    runway: { label: 'Runway', chart: 'line', color: '#7C3AED' },
};
const SNAPSHOT_ORDER = ['revenue', 'net_burn', 'runway'];

const MiniBarChart = ({ series = [], color }) => {
    const w = 100;
    const h = 44;
    const n = series.length || 1;
    const max = Math.max(...series, 1);
    const gap = 3;
    const bw = (w - gap * (n - 1)) / n;
    return (
        <Box component="svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" sx={{ width: '100%', height: 44, display: 'block' }}>
            {series.map((v, i) => {
                const bh = Math.max(2, (v / max) * (h - 2));
                return <rect key={i} x={i * (bw + gap)} y={h - bh} width={bw} height={bh} rx={1.5} fill={color} opacity={i === n - 1 ? 1 : 0.85} />;
            })}
        </Box>
    );
};

const MiniLineChart = ({ series = [], color }) => {
    const uid = React.useId();
    const gid = `spark_${uid}`;
    const w = 100;
    const h = 44;
    const n = series.length;
    if (n < 2) return <Box sx={{ height: 44 }} />;
    const max = Math.max(...series);
    const min = Math.min(...series);
    const span = max - min || 1;
    const pts = series.map((v, i) => [(i / (n - 1)) * w, h - 2 - ((v - min) / span) * (h - 6)]);
    const line = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
    const area = `${line} L ${w} ${h} L 0 ${h} Z`;
    return (
        <Box component="svg" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" sx={{ width: '100%', height: 44, display: 'block' }}>
            <defs>
                <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor={color} stopOpacity="0.28" />
                    <stop offset="1" stopColor={color} stopOpacity="0" />
                </linearGradient>
            </defs>
            <path d={area} fill={`url(#${gid})`} />
            <path d={line} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </Box>
    );
};

const StatCard = ({ meta, data, borderColor }) => (
    <Box sx={{ border: `1px solid ${borderColor}`, borderRadius: '12px', p: 2, height: '100%' }}>
        <Typography sx={{ fontSize: '0.8rem', color: MUTED, mb: 0.5 }}>{meta.label}</Typography>
        <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: HEADING, lineHeight: 1.2 }}>{data?.value ?? '-'}</Typography>
        {data?.delta && (
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, mt: 0.25, color: data?.positive === false ? NEG : POS }}>
                {data?.trend === 'down' ? '↓' : '↑'} {data.delta}
            </Typography>
        )}
        <Box sx={{ mt: 1.5 }}>
            {meta.chart === 'line'
                ? <MiniLineChart series={data?.series || []} color={meta.color} />
                : <MiniBarChart series={data?.series || []} color={meta.color} />}
        </Box>
    </Box>
);

const FileCard = ({ doc, borderColor, iconBg }) => {
    // type_info can be an object {name, code, _id} or a string
    const typeInfoName = typeof doc?.type_info === 'object'
        ? doc?.type_info?.name
        : doc?.type_info;

    const name = typeInfoName || doc?.document_name || doc?.name || doc?.title || 'Document';
    const size = doc?.size || doc?.file_size || doc?.formatted_size;
    const url = doc?.document_url || doc?.file_url || doc?.url;

    return (
        <Box sx={{ border: `1px solid ${borderColor}`, borderRadius: '10px', p: 1.25, display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '6px', bgcolor: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <img src={getFileCategory(name)} width={36} height={36} alt="" style={{ flexShrink: 0 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap title={name} sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#344054' }}>{name}</Typography>
                {size && <Typography sx={{ fontSize: '0.72rem', color: MUTED }}>{size}</Typography>}
            </Box>
            <IconButton size="small" disabled={!url} onClick={() => url && downloadFile(url, name)} aria-label="Download attachment">
                <DownloadDocumentIcon size={18} />
            </IconButton>
        </Box>
    );
};

const ReportDetailPopup = ({ report, onClose }) => {
    const theme = useTheme();
    const border = theme.palette.customColors.gray300;
    const iconBg = theme.palette.customColors.indigo50;

    // Fetch fresh detail for the report; fall back to the object the card passed.
    let { data: details } = FetchReportDetails(report?._id);

    // Keep the detail API's documents BEFORE merging: they carry type_info (and the
    // real document_url). The list API's `report.documents` has neither, and the
    // spread below would silently overwrite them.
    const apiDocuments = details?.documents;

    details = {
        ...details,
        ...report
    }

    const title = details?.report?.name || report?.report_name || report?.name || '-';
    const from = report?.formatted_from_date;
    const to = report?.formatted_to_date;
    const dateRange = from ? (to ? `${from} – ${to}` : from) : '';
    const overviewHtml = details?.report?.overview ?? report?.overview ?? '';
    const documents = (apiDocuments?.length ? apiDocuments : report?.documents) || [];
    const snapshot = report?.performance_snapshot || null;
    const hasSnapshot = snapshot && SNAPSHOT_ORDER.some((k) => snapshot[k]);

    const [anchorEl, setAnchorEl] = React.useState(null);
    const downloadAll = () => {
        documents.forEach((d) => {
            const u = d?.document_url || d?.file_url || d?.url;
            console.log(u, "w")
            if (u) downloadFile(u, d?.document_name || d?.name);
        });
        setAnchorEl(null);
    };

    return (
        <ModalComponent
            open
            onClose={onClose}
            style={{
                width: 'min(600px, 94vw)',
                maxHeight: '90vh',
                bgcolor: '#fff',
                borderRadius: '16px',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                p: 0,
            }}
        >
            {/* Header */}
            <Box sx={{ flexShrink: 0, px: { xs: 2.5, md: 3.5 }, pt: { xs: 2.5, md: 3 }, pb: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, borderBottom: `1px solid ${border}` }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.1rem', md: '1.25rem' }, color: HEADING }}>{title}</Typography>
                    {dateRange && <Typography sx={{ fontSize: '0.85rem', color: MUTED }}>({dateRange})</Typography>}
                </Box>
                {/* Top-right controls: optional Download kebab (only with attachments) + Close. */}
                <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {documents.length > 0 && (
                        <>
                            <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} aria-label="more">
                                <MoreVert fontSize="small" />
                            </IconButton>
                            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                                <MenuItem onClick={downloadAll}>Download attachments</MenuItem>
                            </Menu>
                        </>
                    )}
                    <IconButton size="small" onClick={onClose} aria-label="Close">
                        <Close fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {/* Scrollable content */}
            <Box sx={{ px: { xs: 2.5, md: 3.5 }, py: 3, overflowY: 'auto', flex: 1, minHeight: 0 }}>
                {/* Performance Snapshot — conditional: hidden until the backend supplies performance_snapshot */}
                {hasSnapshot && (
                    <Box sx={{ mb: 3 }}>
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#344054', mb: 1.5 }}>Performance Snapshot</Typography>
                        <Grid container spacing={2}>
                            {SNAPSHOT_ORDER.map((key) => {
                                const metric = snapshot[key];
                                if (!metric) return null;
                                return (
                                    <Grid item xs={12} sm={4} key={key}>
                                        <StatCard meta={SNAPSHOT_META[key]} data={metric} borderColor={border} />
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </Box>
                )}

                {/* Body — full overview HTML, not clamped */}
                {overviewHtml
                    ? (
                        <Box
                            sx={{
                                color: BODY,
                                fontSize: '0.9rem',
                                lineHeight: 1.7,
                                '& p': { m: 0, mb: 1.5 },
                                '& u': { textDecorationColor: HEADING },
                                '& ul, & ol': { pl: 3, m: 0, mb: 1.5 },
                                '& li': { mb: 0.5 },
                                '& h1, & h2, & h3, & h4': { color: HEADING, fontSize: '0.95rem', fontWeight: 600, mt: 2, mb: 1 },
                                '& strong, & b': { color: HEADING },
                                '& a': { color: '#5046E5' },
                            }}
                            dangerouslySetInnerHTML={{ __html: overviewHtml }}
                        />
                    )
                    : <Typography sx={{ color: MUTED, fontSize: '0.9rem' }}>No overview available.</Typography>}

                {/* Attachments */}
                {documents.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <Typography sx={{ fontSize: '0.8rem', color: MUTED, mb: 1 }}>Attachments</Typography>
                        <Grid container spacing={2}>
                            {documents.map((doc, i) => (
                                <Grid item xs={12} sm={6} key={i}>
                                    <FileCard doc={doc} borderColor={border} iconBg={iconBg} />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </Box>

        </ModalComponent>
    );
};

export default ReportDetailPopup;
